import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passwordRecoveryPending, setPasswordRecoveryPending] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let timeoutCleared = false;
    
    // Set a timeout to prevent infinite loading
    const loadingTimeout = setTimeout(async () => {
      if (isMounted && !timeoutCleared) {
        console.warn('Auth loading timeout - forcing loading to false and setting user');
        setLoading(false);
        
        // Immediately get user and set state
        try {
          const { data: { user: currentUser }, error: getUserError } = await supabase.auth.getUser();
          console.log('Timeout: Current user:', currentUser?.id, 'error:', getUserError);
          
          if (currentUser && isMounted) {
            console.log('Timeout: Setting user state immediately');
            setUser(currentUser);
            
            // Try to get or create profile (don't block)
            (async () => {
              try {
                const { data: existingProfile, error: profileError } = await supabase
                  .from('user_profiles')
                  .select('*')
                  .eq('id', currentUser.id)
                  .single();
              
                console.log('Timeout: Profile check:', existingProfile ? 'found' : 'not found', 'error:', profileError?.code);
              
                if (!existingProfile || profileError?.code === 'PGRST116') {
                  console.log('Timeout: Creating default profile');
                  const defaultProfile = {
                    id: currentUser.id,
                    email: currentUser.email || '',
                    role: currentUser?.user_metadata?.role || 'player'
                  };
                  
                  const { data: newProfile, error: insertError } = await supabase
                    .from('user_profiles')
                    .insert(defaultProfile)
                    .select()
                    .single();
                  
                  console.log('Timeout: Insert result:', newProfile ? 'success' : 'failed', 'error:', insertError?.message);
                  
                  if (isMounted) {
                    setUserProfile(newProfile || defaultProfile);
                  }
                } else if (isMounted) {
                  console.log('Timeout: Using existing profile');
                  setUserProfile(existingProfile);
                }
              } catch (error) {
                console.error('Timeout: Error in profile creation:', error);
                // Set default profile anyway
                if (isMounted) {
                  setUserProfile({
                    id: currentUser.id,
                    email: currentUser.email || '',
                    role: currentUser?.user_metadata?.role || 'player'
                  });
                }
              }
            })();
          }
        } catch (error) {
          console.error('Timeout: Error getting user:', error);
        }
      }
    }, 3000); // 3 second timeout

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Initial session check:', session?.user?.id || 'no session');
      if (!isMounted) return;
      
      // Set user immediately
      setUser(session?.user ?? null);
      
      if (session?.user) {
        console.log('Loading profile for user:', session.user.id);
        // Load profile but don't block
        loadUserProfile(session.user.id).finally(() => {
          console.log('Profile load finished');
          if (isMounted) {
            setLoading(false);
            timeoutCleared = true;
            clearTimeout(loadingTimeout);
          }
        });
      } else {
        console.log('No session found, setting loading to false');
        setLoading(false);
        timeoutCleared = true;
        clearTimeout(loadingTimeout);
      }
    }).catch((error) => {
      console.error('Error getting session:', error);
      if (isMounted) {
        setLoading(false);
        timeoutCleared = true;
        clearTimeout(loadingTimeout);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;
      console.log('Auth state changed:', event, session?.user?.id || 'no session');

      // Recovery links establish a session and fire PASSWORD_RECOVERY —
      // App must show the "set new password" UI when this happens.
      if (event === 'PASSWORD_RECOVERY') {
        console.log('[PASSWORD-RESET] PASSWORD_RECOVERY event received');
        setPasswordRecoveryPending(true);
      }

      setUser(session?.user ?? null);
      if (session?.user) {
        // Load profile but don't block
        loadUserProfile(session.user.id).finally(() => {
          if (isMounted) {
            setLoading(false);
          }
        });
        // Auto-link any past game participation to this account
        linkPastGamesToUser(session.user.id, session.user.email);
      } else {
        setUserProfile(null);
        setLoading(false);
      }
      timeoutCleared = true;
      clearTimeout(loadingTimeout);
    });

    return () => {
      isMounted = false;
      timeoutCleared = true;
      subscription.unsubscribe();
      clearTimeout(loadingTimeout);
    };
  }, []);

  const linkPastGamesToUser = async (userId, email) => {
    try {
      const { data, error } = await supabase.rpc('link_games_to_user', {
        p_user_id: userId,
        p_email: email
      });
      if (error) {
        console.warn('Could not link past games:', error.message);
      } else if (data && (data.find_image_games_linked > 0 || data.main_games_linked > 0)) {
        console.log('Linked past games to account:', data);
      }
    } catch (err) {
      console.warn('link_games_to_user call failed:', err.message);
    }
  };

  const loadUserProfile = async (userId) => {
    try {
      console.log('Loading user profile for:', userId);
      const userResult = await supabase.auth.getUser();
      const currentUser = userResult.data.user;
      const userMetaRole = currentUser?.user_metadata?.role;
      
      // Try to load profile with retries
      let retries = 3;
      let data = null;
      let error = null;

      while (retries > 0) {
        const result = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .single();

        data = result.data;
        error = result.error;

        if (data && !error) {
          break;
        }

        // If profile doesn't exist, wait a bit and retry (trigger might be slow)
        if (error?.code === 'PGRST116') {
          console.log(`Profile not found, retrying... (${retries} attempts left)`);
          await new Promise(resolve => setTimeout(resolve, 1000));
          retries--;
        } else {
          break;
        }
      }

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading user profile:', error);
        setUserProfile(null);
      } else if (data) {
        console.log('Profile loaded:', data);
        // If auth metadata has a different role, sync it
        if (userMetaRole && data.role !== userMetaRole) {
          try {
            const { data: updated, error: updateError } = await supabase
              .from('user_profiles')
              .update({ role: userMetaRole })
              .eq('id', userId)
              .select()
              .single();
            if (updateError) {
              console.error('Error syncing role to profile:', updateError);
              setUserProfile(data);
            } else {
              setUserProfile(updated);
            }
          } catch (syncErr) {
            console.error('Error during role sync:', syncErr);
            setUserProfile(data);
          }
        } else {
          setUserProfile(data);
        }
      } else {
        // Profile doesn't exist, create it
        console.log('Profile not found, creating...');
        const email = currentUser?.email || '';
        
        const computedRole = userMetaRole || 'player';
        
        const { data: newProfile, error: createError } = await supabase
          .from('user_profiles')
          .insert({
            id: userId,
            email: email,
            role: computedRole
          })
          .select()
          .single();
        
        if (createError) {
          console.error('Error creating profile:', createError);
          setUserProfile(null);
        } else {
          console.log('Profile created:', newProfile);
          setUserProfile(newProfile);
        }
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      setUserProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email, password, fullName = '', role = 'player') => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: role || 'player',
          },
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) {
        console.error('Sign up error:', error);
        throw error;
      }

      // Profile will be created automatically by the database trigger
      if (data.user) {
        // Proactively upsert the profile with the chosen role
        try {
          await supabase
            .from('user_profiles')
            .upsert({
              id: data.user.id,
              email: data.user.email || email,
              role: role || 'player'
            });
        } catch (upsertErr) {
          console.warn('Profile upsert after signup failed (will rely on trigger):', upsertErr);
        }

        // If a session is returned (email confirmations disabled globally), still allow sign-in,
        // but host flows will be gated on email_confirmed_at.
        if (data.session) {
          setUser(data.user);
          await new Promise(resolve => setTimeout(resolve, 1500));
          await loadUserProfile(data.user.id);
          return { 
            data, 
            error: null,
            needsEmailConfirmation: false
          };
        }

        // No session means email confirmation is required; surface that to caller.
        return { 
          data, 
          error: null,
          needsEmailConfirmation: true
        };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      // Check if it's specifically an email sending error
      if (error.message && error.message.includes('email')) {
        // User might still be created, try to sign them in
        try {
          const { data: signInData } = await supabase.auth.signInWithPassword({
            email,
            password,
          });
          if (signInData.user) {
            await loadUserProfile(signInData.user.id);
            return { 
              data: signInData, 
              error: null,
              warning: 'Account created but email confirmation failed. You are now signed in.'
            };
          }
        } catch (signInError) {
          console.error('Auto sign-in failed:', signInError);
        }
      }
      return { data: null, error };
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        await loadUserProfile(data.user.id);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
      setUserProfile(null);
      return { error: null };
    } catch (error) {
      console.error('Sign out error:', error);
      return { error };
    }
  };

  const isHost = () => {
    return userProfile?.role === 'host';
  };

  const isPlayer = () => {
    return userProfile?.role === 'player';
  };

  const resetPassword = async (email) => {
    try {
      // Dedicated path so App can show the set-new-password UI after the email link.
      // Must also be listed under Supabase Auth → URL Configuration → Redirect URLs.
      const redirectUrl = `${window.location.origin}/reset-password`;
      
      console.log('[PASSWORD-RESET] Attempting to send reset email to:', email);
      console.log('[PASSWORD-RESET] Redirect URL:', redirectUrl);
      
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        console.error('[PASSWORD-RESET] Supabase error:', {
          message: error.message,
          status: error.status,
          name: error.name,
          fullError: error
        });
        throw error;
      }

      console.log('[PASSWORD-RESET] Success! Email sent.');
      return { data, error: null };
    } catch (error) {
      console.error('[PASSWORD-RESET] Password reset error:', error);
      return { data: null, error };
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      console.log('[PASSWORD-UPDATE] Attempting to update password');
      
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        console.error('[PASSWORD-UPDATE] Supabase error:', {
          message: error.message,
          status: error.status,
          name: error.name,
          fullError: error
        });
        throw error;
      }

      console.log('[PASSWORD-UPDATE] Success! Password updated.');
      setPasswordRecoveryPending(false);
      return { data, error: null };
    } catch (error) {
      console.error('[PASSWORD-UPDATE] Password update error:', error);
      return { data: null, error };
    }
  };

  const clearPasswordRecovery = () => {
    setPasswordRecoveryPending(false);
  };

  const refreshUserProfile = async () => {
    if (user?.id) {
      await loadUserProfile(user.id);
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    passwordRecoveryPending,
    clearPasswordRecovery,
    isHost,
    isPlayer,
    refreshUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

